<template>
  <div>
    <wb-modal
      width="80"
      blocked
      @close="emit('close')"
    >
      <wb-progress-bar
        class="mb-16"
        :val="currentStatesCount"
        :max="totalStatesCount"
        bar-color="var(--primary-500)"
      />
      <transition :name="data.stepDirection">
        <div v-if="isCurrentState('locationChargers')" class="content-step">
          <location-chargers
            :ref="setRef('locationChargers')"
            v-model:groupUid="data.chargersToAdd.location.groupUid"
            v-model:location="data.editLocation"
            v-model:country="data.chargersToAdd.location.country"
            v-model:timezoneId="data.chargersToAdd.location.timezoneId"
            v-model:loading="data.actionsDisabled"
            @on-create-location="methods.handleCustomButton('editLocation')"
          />
        </div>

        <div v-else-if="isCurrentState('editLocation')" class="content-step">
          <edit-location
            :ref="setRef('editLocation')"
            :location-id="data.editLocation?.id"
            @on-edit="methods.assignLocation"
          />
        </div>

        <div v-else-if="isCurrentState('addChargers')" class="content-step">
          <add-chargers
            id="chargersSelectors"
            :ref="setRef('addChargers')"
            v-model="data.chargersToAdd.chargers"
            v-model:disabled="data.actionsDisabled"
            :location="data.chargersToAdd.location"
          />
        </div>

        <div v-else-if="isCurrentState('configurePayments')" class="content-step">
          <configure-payments
            :ref="setRef('configurePayments')"
            :chargers="data.chargersToAdd.chargers"
            @on-configure-payments="emit('on-configure-payments')"
          />
        </div>
      </transition>

      <template #actions>
        <div class="button-actions">
          <wb-button
            v-if="isCurrentState('locationChargers')"
            data-test-id="cancelButton"
            variant="white"
            outlined
            :label="i18n.t('mywb.common.cancel')"
            @click="emit('close')"
          />
          <wb-button
            v-if="!isCurrentState('locationChargers') && !isCurrentState('configurePayments')"
            data-test-id="backButton"
            icon="arrow_back"
            variant="white"
            outlined
            class="mr-24"
            :label="i18n.t('mywb.common.back')"
            @click="methods.handleBackButton"
          />
          <span v-else />
          <wb-button
            data-test-id="nextButton"
            :label="i18n.t('mywb.common.continue')"
            icon="arrow_forward"
            icon-position="right"
            :disabled="data.actionsDisabled"
            :loading="data.loading"
            @click="methods.handleNextButton"
          />
        </div>
      </template>
    </wb-modal>
  </div>
</template>

<script setup lang="ts">
import AddChargers from '@/components/addChargers/AddChargers.vue'
import LocationChargers from '@/components/addChargers/LocationChargers.vue'
import EditLocation from '@/components/addChargers/EditLocation.vue'
import ConfigurePayments from '@/components/addChargers/ConfigurePayments.vue'

import { trackDataScreen } from '@/engine/metrics/trackDataManager'
import { useI18n } from '@/hooks/useI18n.hook'
import { useTemplateRef, useToast } from '@wallbox/toolkit-ui'
import { reactive, computed } from 'vue'
import { useStateMachine } from '@wallbox/hooks'
import { permissions } from '@/engine/clients'
import state from '@/state'
import { EnumPlans } from '@/utilities/plans'
import { hasFeatureFlagActive } from '@/engine/featureFlags/featureFlags.state'
import { FEATURE_FLAGS } from '@/engine/featureFlags/flags'
import { useRouter, useRoute } from 'vue-router'
import type { ChargerToAdd } from '@/core/charger'
import type { LocationAddingChargers, Location } from '@/core/location'
import { injectStrict, INTERNATIONAL_USE_CASES, LOCATION_USE_CASES } from '@/engine/injectors'
import type { Uuid } from '@/core/shared'

const locationUseCases = injectStrict(LOCATION_USE_CASES)
const internationalUseCases = injectStrict(INTERNATIONAL_USE_CASES)

const { refs, setRef } = useTemplateRef()
const i18n = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()

interface Data {
  chargersToAdd: {
    chargers: ChargerToAdd[],
    location: LocationAddingChargers
  },
  stepDirection: 'slide-left' | 'slide-right',
  loading: boolean,
  editLocation?: Location,
  showToastSuccess: boolean,
  actionsDisabled: boolean
}

const initialCharger: ChargerToAdd =
  {
    serial: {
      value: ''
    },
    puk: {
      value: ''
    },
    added: false
  }

const data = reactive<Data>({
  chargersToAdd: {
    chargers: [initialCharger],
    location: {
      groupUid: undefined,
      country: undefined,
      timezoneId: undefined
    }
  },
  stepDirection: 'slide-left',
  loading: false,
  editLocation: undefined,
  showToastSuccess: false,
  actionsDisabled: false
})

const compute = reactive({
  isLocationCompleted: computed(() => {
    // With the basic-plan-feature-parity flag, Basic can skip the location: only country and timezone are required
    const isBasicWithoutLocation = state.organizations.isPlan()([EnumPlans.BASIC]) &&
      hasFeatureFlagActive(FEATURE_FLAGS.BASIC_PLAN_FEATURE_PARITY) &&
      !data.editLocation
    if (!permissions.canSeeLocations || isBasicWithoutLocation) {
      return data.chargersToAdd.location.country && data.chargersToAdd.location.timezoneId
    }

    if (!data.editLocation) return false

    const { country, address, city, latitude, longitude, zipCode } = data.editLocation

    return country && address && city && latitude && longitude && zipCode
  })
})

const { currentStateKey, isCurrentState, send, currentStatesCount, totalStatesCount } = useStateMachine({
  initial: 'locationChargers',
  states: {
    locationChargers: {
      transitions: {
        editLocation: 'editLocation',
        next: 'addChargers'
      }
    },

    editLocation: {
      transitions: {
        next: 'addChargers',
        back: 'locationChargers'
      }
    },

    addChargers: {
      transitions: {
        next: 'configurePayments',
        back: 'locationChargers'
      },
      on: {
        back: () => {
          data.chargersToAdd.chargers = [initialCharger]
        }
      }
    },

    configurePayments: {
      transitions: {
        back: 'addChargers',
        next: 'configurePayments'
      },
      on: {
        next: () => {
          toast.success(i18n.t('mywb.add-chargers.chargers-added'))
          emit('close')
        }
      }
    }
  }
})

const emit = defineEmits([
  'close',
  'on-configure-payments',
  'on-update-chargers'
])

const methods = {
  handleBackButton () {
    data.stepDirection = 'slide-left'
    send('back')
  },

  async handleNextButton () {
    data.loading = true
    if (refs[currentStateKey.value] && await refs[currentStateKey.value].validateForm()) {
      data.stepDirection = 'slide-right'

      if (isCurrentState('locationChargers') && !compute.isLocationCompleted) {
        send('editLocation')
        data.loading = false
        return
      }

      if (isCurrentState('addChargers')) {
        emit('on-update-chargers')
        if (!permissions.showPaymentsEngage) {
          emit('close')
          data.loading = false
          return
        }
      }
      send('next')
    }
    data.loading = false
  },

  handleCustomButton (stateName: 'editLocation' | 'next' | 'back') {
    data.stepDirection = 'slide-right'
    data.editLocation = undefined
    send(stateName)
  },

  async assignLocation (locationId: string) {
    const location = await locationUseCases.getLocation(locationId)
    if (!location) return

    let timezoneId = ''
    if (location.latitude && location.longitude) {
      const timezone = await internationalUseCases.getTimezoneByCoords({
        lat: location.latitude,
        lng: location.longitude
      })

      if (!timezone) throw new Error('timezone empty')

      timezoneId = timezone
    }

    data.chargersToAdd.location.groupUid = location.groupUid
    data.chargersToAdd.location.country = location.country
    data.chargersToAdd.location.timezoneId = timezoneId
  }
}

function created () {
  trackDataScreen('add-chargers')
  if (route.query['group-uid']) {
    data.chargersToAdd.location.groupUid = route.query['group-uid'].toString() as Uuid
    router.push({ query: {} }).catch(() => {})
  }
}

created()
</script>

<style lang="postcss" scoped>
:deep(.button-actions) {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.modal-grid) {
  min-height: calc(100vh - 8rem);
}

:deep(.modal-content) {
  overflow-x: hidden;
  max-height: calc(100vh - 18rem) !important;
}

.content-step {
  @media (--tablet) {
    width: calc(80rem - 8rem);
  }
}

.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 360ms ease-out;
  position: absolute;
}

.slide-right-enter-from {
  transform: translateX(80rem);
}

.slide-right-leave-to {
  transform: translateX(-80rem);
}

.slide-left-enter-from {
  transform: translateX(-80rem);
}

.slide-left-leave-to {
  transform: translateX(80rem);
}
</style>
